import '@material/web/icon/icon.js'
import '@material/web/chips/assist-chip.js'
import './ox-page-action-overlay-template.js'

import { css, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import throttle from 'lodash-es/throttle.js'
import { openOverlay } from '@operato/layout'
import { RouteController } from '@operato/shell'
import { sleep } from '@operato/utils'
import { ButtonContainerStyles } from '@operato/styles'

@customElement('ox-page-action-context-bar')
export class PageActionContextBar extends LitElement {
  static get styles() {
    return [
      ButtonContainerStyles,
      css`
        :host {
          display: flex;
          position: relative;
          overflow-x: clip;
          overflow-y: hidden;

          text-transform: capitalize;
        }

        :host *:focus {
          outline: none;
        }

        [container] {
          flex: 1;

          display: flex;
          overflow: hidden;
          gap: var(--spacing-small);
          padding: 2px; /* only for button shadow */
          align-items: center;
        }

        [more] {
          position: absolute;
          bottom: 0;
          right: 0;
          color: var(--md-sys-color-inverse-on-surface);
          background-color: var(--md-sys-color-inverse-surface);
          box-shadow: -2px 0px 2px 1px rgba(0, 0, 0, 0.2);
          padding: var(--context-action-bar-more-button-padding, 11px 10px);
          font-size: 2rem;
          z-index: 10;
        }

        select,
        input[type='text'] {
          box-sizing: border-box;
          padding: 0 var(--spacing-small);
          height: var(--form-element-height-medium);
          border: 1px solid var(--md-sys-color-outline);
          border-radius: var(--md-sys-shape-corner-small);
          color: var(--md-sys-color-on-primary);
          background-color: var(--md-sys-color-on-primary);
          font-size: var(--md-sys-typescale-label-large-size, 0.875rem);
          outline: none;
        }

        md-icon[working] {
          animation: rotate 1s linear infinite;
        }

        md-assist-chip[icon-only] {
          --md-assist-chip-trailing-space: 0;
        }

        @media screen and (max-width: 460px) {
          [container] {
            padding: 0;
            gap: 0;
            --md-assist-chip-container-shape: 0;
          }
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `
    ]
  }

  private _route = new RouteController(this)

  @property({ type: Array }) actions: {
    title: string
    action: () => void
    icon: string
    type: string
    select: string[]
    href?: string
    emphasis?: boolean
  }[] = []

  @state() private overflown: boolean = false
  @query('[container]') container?: HTMLDivElement

  private overflownUpdaterThrottle: any
  private resizeCallback: any

  get overflownUpdater() {
    if (!this.overflownUpdaterThrottle) {
      this.overflownUpdaterThrottle = throttle(() => {
        this.overflown = !!this.container && this.container.scrollWidth > this.container.clientWidth
      }, 100)
    }

    return this.overflownUpdaterThrottle
  }

  connectedCallback() {
    super.connectedCallback()

    this.resizeCallback = () => this.overflownUpdater()

    window.addEventListener('resize', this.resizeCallback)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('resize', this.resizeCallback)
  }

  render() {
    return this.actions.length > 0
      ? html`
          <div container>${this.renderActions()}</div>
          ${this.overflown ? html` <md-icon more @click=${this.showMore.bind(this)}>more_horiz</md-icon> ` : html``}
        `
      : nothing
  }

  renderActions() {
    return html`
      ${this.actions.map(($action, idx) => {
        let { type, title, icon, action, select, href, emphasis } = $action as any
        let { raised, danger } = emphasis || {}

        return type == 'text'
          ? html` <span @click=${action}>${title}</span> `
          : type == 'select' || (select && select.length > 0)
            ? html`
                <select @change=${action}>
                  ${select.map((option: any) => html` <option>${option}</option> `)}
                </select>
              `
            : type == 'link'
              ? html` <a href=${href}>${title}</a> `
              : html`
                  <md-assist-chip
                    ?icon-only=${type == 'icon' || !title}
                    ?elevated=${raised}
                    ?danger=${danger}
                    label=${title}
                    @click=${async (e: MouseEvent) => {
                      /* all actions should be bloked for a second */
                      const button = e.currentTarget as any
                      const iconElement = button.querySelector('[slot="icon"]')
                      const actionConfig = this.actions[idx]

                      if (iconElement) {
                        icon = actionConfig.icon
                        actionConfig.icon = 'refresh'
                        this.requestUpdate()
                        iconElement.setAttribute('working', true)
                      }
                      button.disabled = true

                      try {
                        await action()
                      } finally {
                        await sleep(1000) /* waiting for additionla 1 second */

                        /* because icon can be changed after sleep, don't use closure 'icon'. */
                        if (iconElement) {
                          actionConfig.icon = icon
                          this.requestUpdate()
                          iconElement.removeAttribute('working')
                        }
                        button.disabled = false
                      }
                    }}
                  >
                    <md-icon slot="icon">${this.actions[idx].icon}</md-icon>
                  </md-assist-chip>
                `
      })}
    `
  }

  async updated(changed: PropertyValues<this>) {
    if (changed.has('actions')) {
      requestAnimationFrame(this.overflownUpdater)
    }
  }

  protected willUpdate() {
    const { actions } = this._route.context || {}

    if (actions) {
      this.actions = actions.filter((action: any) => !!action)
    }
  }

  showMore() {
    openOverlay('context-toolbar-overlay', {
      template: html`<ox-page-action-overlay-template></ox-page-action-overlay-template>`
    })
  }
}
